import type { IssuanceSnapshotContract, Locale } from "@chams-pay/contracts";
import {
  documentMessages,
  formatDisplayDate,
  formatWholeFigure,
  normalizeSchoolLevelLabel,
} from "@chams-pay/contracts";
import { Bidi } from "@chams-pay/design-system";
import { toPng } from "html-to-image";
import { jsPDF } from "jspdf";
import { Fragment } from "react";
import type { ReactNode } from "react";

export const documentUiBoundary = {
  authority: "semantic-financial-documents",
  phaseIntroduced: 2,
} as const;

function visibleFinancialDocument(): HTMLElement {
  const documents = [...document.querySelectorAll<HTMLElement>(".financial-document")];
  const visible = documents.filter((candidate) => candidate.getClientRects().length > 0);
  const financialDocument = visible.at(-1);
  if (!financialDocument) throw new Error("No visible financial document");
  return financialDocument;
}

function receiptStyles(): string {
  return [...document.head.querySelectorAll("style, link[rel='stylesheet']")]
    .map((node) => node.outerHTML)
    .join("\n");
}

function receiptFilename(financialDocument: HTMLElement): string {
  const reference = financialDocument.dataset.reference?.replace(/[^A-Za-z0-9-]/g, "");
  const locale = financialDocument.lang === "fr" ? "fr" : "ar";
  return reference ? `receipt-${reference}-${locale}` : `receipt-${locale}`;
}

async function waitForReceiptAssets(root: Document | HTMLElement): Promise<void> {
  const ownerDocument =
    root.nodeType === 9 ? (root as Document) : (root as HTMLElement).ownerDocument;
  await ownerDocument.fonts.ready;
  const images = [...root.querySelectorAll<HTMLImageElement>("img")];
  await Promise.all(
    images.map(async (image) => {
      if (!image.complete)
        await new Promise<void>((resolve) => {
          image.addEventListener("load", () => resolve(), { once: true });
          image.addEventListener("error", () => resolve(), { once: true });
        });
      await image.decode().catch(() => undefined);
    }),
  );
}

export function printFinancialDocument(): void {
  const financialDocument = visibleFinancialDocument();
  const filename = receiptFilename(financialDocument);
  const originalTitle = document.title;
  const frame = document.createElement("iframe");
  frame.title = financialDocument.lang === "ar" ? "طباعة وصل الأداء" : "Impression du reçu";
  frame.style.cssText =
    "position:fixed;inset-block-start:0;inset-inline-start:-10000px;width:210mm;height:297mm;border:0;pointer-events:none";
  const cleanup = () => {
    document.title = originalTitle;
    frame.remove();
  };
  frame.addEventListener(
    "load",
    () => {
      const frameWindow = frame.contentWindow;
      const frameDocument = frame.contentDocument;
      if (!frameWindow || !frameDocument) {
        cleanup();
        return;
      }
      void waitForReceiptAssets(frameDocument).then(() => {
        frameDocument.title = filename;
        document.title = filename;
        frameWindow.onafterprint = cleanup;
        frameWindow.focus();
        frameWindow.print();
        window.setTimeout(cleanup, 60_000);
      });
    },
    { once: true },
  );
  frame.srcdoc = `<!doctype html>
<html lang="${financialDocument.lang}" dir="${financialDocument.dir}">
<head>
  <meta charset="utf-8">
  <title>${filename}</title>
  <base href="${document.baseURI}">
  ${receiptStyles()}
</head>
<body>${financialDocument.outerHTML}</body>
</html>`;
  document.body.append(frame);
}

export async function renderDocumentToPng(element: HTMLElement, pixelRatio = 3): Promise<string> {
  await waitForReceiptAssets(element);
  const dataUrl = await toPng(element, {
    backgroundColor: "#ffffff",
    cacheBust: true,
    pixelRatio,
    width: element.scrollWidth,
    height: element.scrollHeight,
  });
  if (!dataUrl.startsWith("data:image/png") || dataUrl.length < 1_024)
    throw new Error("Document image capture is empty");
  return dataUrl;
}

function normalizedPdfFilename(filename: string): string {
  return filename.endsWith(".pdf") ? filename : `${filename}.pdf`;
}

async function financialDocumentPdfBlob(): Promise<Blob> {
  const financialDocument = visibleFinancialDocument();
  const host = document.createElement("div");
  host.style.cssText =
    "position:fixed;inset-block-start:0;inset-inline-start:0;width:180mm;height:267mm;overflow:hidden;background:#fff;pointer-events:none;z-index:-2147483647";
  const clone = financialDocument.cloneNode(true) as HTMLElement;
  clone.dataset.renderMode = "a4";
  clone.style.inlineSize = "180mm";
  clone.style.blockSize = "267mm";
  clone.style.minBlockSize = "267mm";
  clone.style.aspectRatio = "auto";
  clone.style.borderRadius = "0";
  clone.style.boxShadow = "none";
  clone.style.setProperty("container-type", "normal");
  host.append(clone);
  document.body.append(host);
  try {
    const image = await renderDocumentToPng(clone);
    const pdf = new jsPDF({ format: "a4", orientation: "portrait", unit: "mm" });
    pdf.addImage(image, "PNG", 15, 15, 180, 267, undefined, "FAST");
    return pdf.output("blob");
  } finally {
    host.remove();
  }
}

function downloadPdfBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = normalizedPdfFilename(filename);
  link.style.display = "none";
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function downloadFinancialDocumentPdf(filename: string): Promise<void> {
  downloadPdfBlob(await financialDocumentPdfBlob(), filename);
}

const receiptCopy = {
  ar: {
    title: "وصل أداء",
    school: "مؤسسة شمس العلوم",
    schoolLatin: "للتعليم الخصوصي",
    ministry: "المملكة المغربية، وزارة التربية الوطنية والتعليم الأولي والرياضة",
    studentDetails: "بيانات التلميذ",
    paymentDetails: "بيانات الأداء",
    services: "تفاصيل الخدمات",
    fullName: "الاسم الكامل:",
    class: "المستوى:",
    academicYear: "السنة الدراسية:",
    reference: "رقم الأداء:",
    received: "التاريخ:",
    settled: "تاريخ الأداء:",
    periods: "الأشهر المؤداة:",
    method: "طريقة الأداء:",
    paid: "مؤدى",
    signature: "توقيع الإدارة",
    total: "المبلغ الإجمالي:",
    currency: "درهم",
    month: "شهر",
    months: "أشهر",
    registrationPeriod: "التسجيل",
    registration: "واجبات التسجيل",
    tuition: "واجبات التمدرس",
    transport: "النقل المدرسي",
    accompaniment: "المرافقة",
    address: "277، شارع ابن سينا، حي تيرت",
  },
  fr: {
    title: "Reçu de paiement",
    school: "Établissement Chams Al-Ouloum",
    schoolLatin: "pour l'enseignement privé",
    ministry: "Royaume du Maroc, Ministère de l’Éducation Nationale, du Préscolaire et des Sports",
    studentDetails: "Informations de l’élève",
    paymentDetails: "Informations du paiement",
    services: "Détail des services",
    fullName: "Nom complet :",
    class: "Niveau :",
    academicYear: "Année scolaire :",
    reference: "Nº de paiement :",
    received: "Date :",
    settled: "Date de règlement :",
    periods: "Mois réglés :",
    method: "Mode de paiement :",
    paid: "PAYÉ",
    signature: "Signature de l’administration",
    total: "Montant total :",
    currency: "MAD",
    month: "mois",
    months: "mois",
    registrationPeriod: "Inscription",
    registration: "Frais d’inscription",
    tuition: "Frais de scolarité",
    transport: "Transport scolaire",
    accompaniment: "Accompagnement",
    address: "277, avenue Ibn Sina, quartier Tirit",
  },
} as const;

function ContactIcon({ kind }: { readonly kind: "home" | "mail" | "phone" }) {
  if (kind === "home")
    return (
      <svg className="financial-document__icon" viewBox="0 0 24 24" fill="none">
        <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
        <path d="M9 22V12h6v10" />
      </svg>
    );
  if (kind === "mail")
    return (
      <svg className="financial-document__icon" viewBox="0 0 24 24" fill="none">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="m22 6-10 7L2 6" />
      </svg>
    );
  return (
    <svg className="financial-document__icon" viewBox="0 0 24 24" fill="none">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.95.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.86.58 2.81.7A2 2 0 0 1 22 16.92Z" />
    </svg>
  );
}

function InfoRow({
  label,
  children,
  ltr = false,
  className,
}: {
  readonly label: ReactNode;
  readonly children: ReactNode;
  readonly ltr?: boolean;
  readonly className?: string;
}) {
  return (
    <div className={`financial-document__info-row${className ? ` ${className}` : ""}`}>
      <dt>{label}</dt>
      <dd className={ltr ? "financial-document__number" : undefined} dir={ltr ? "ltr" : undefined}>
        {children}
      </dd>
    </div>
  );
}

function academicYearFor(allocations: IssuanceSnapshotContract["allocations"]): string | null {
  for (const allocation of allocations) {
    if (allocation.family !== "registration") continue;
    const match = /(\d{4})-(\d{4})$/.exec(allocation.period);
    if (match && Number(match[2]) === Number(match[1]) + 1) return `${match[1]}-${match[2]}`;
  }
  for (const allocation of allocations) {
    const match = /^(\d{4})-(\d{2})$/.exec(allocation.period);
    if (!match) continue;
    const year = Number(match[1]);
    const month = Number(match[2]);
    if (month < 1 || month > 12) continue;
    const startYear = month >= 9 ? year : year - 1;
    return `${startYear}-${startYear + 1}`;
  }
  return null;
}

function paidPeriodLabels(
  allocations: IssuanceSnapshotContract["allocations"],
  locale: Locale,
  registrationLabel: string,
): readonly { readonly key: string; readonly label: string }[] {
  const registration = allocations.some((allocation) => allocation.family === "registration");
  const months = [
    ...new Set(
      allocations
        .filter((allocation) => allocation.family !== "registration")
        .map((allocation) => allocation.period)
        .filter((period) => /^(\d{4})-(\d{2})$/.test(period)),
    ),
  ].sort();
  const formatter = new Intl.DateTimeFormat(
    locale === "ar" ? "ar-MA-u-nu-latn" : "fr-MA-u-nu-latn",
    { month: "long", timeZone: "UTC" },
  );
  return [
    ...(registration ? [{ key: "registration", label: registrationLabel }] : []),
    ...months.map((period) => ({
      key: period,
      label: formatter.format(new Date(`${period}-01T12:00:00Z`)),
    })),
  ];
}

export function FinancialDocument({
  locale,
  snapshot,
  lifecycle = snapshot.kind === "pending-acknowledgement" ? "pending" : "settled",
  settlementDate,
  cancellationReason,
}: {
  readonly locale: Locale;
  readonly snapshot: IssuanceSnapshotContract;
  readonly lifecycle?: "pending" | "settled" | "rejected" | "cancelled";
  readonly settlementDate?: string;
  readonly cancellationReason?: string;
}) {
  const text = documentMessages(locale);
  const copy = receiptCopy[locale];
  const pending = snapshot.kind === "pending-acknowledgement";
  const method = text[snapshot.method.method];
  const pendingInstrumentTitle =
    snapshot.method.method === "cheque" ? text.pendingCheque : text.pendingTransfer;
  const lifecycleLabel =
    lifecycle === "cancelled"
      ? text.cancelled
      : lifecycle === "rejected"
        ? text.rejected
        : lifecycle === "pending"
          ? text.pending
          : copy.paid;
  const studentName = locale === "ar" ? snapshot.student.nameAr : snapshot.student.nameFr;
  const amount = (value: string) => formatWholeFigure(BigInt(value));
  const academicYear = academicYearFor(snapshot.allocations);
  const paidPeriods = paidPeriodLabels(snapshot.allocations, locale, copy.registrationPeriod);
  const serviceRows = (["registration", "tuition", "transport", "accompaniment"] as const).flatMap(
    (family) => {
      const allocations = snapshot.allocations.filter((allocation) => allocation.family === family);
      if (allocations.length === 0) return [];
      const periods = new Set(allocations.map((allocation) => allocation.period)).size;
      const label =
        family === "registration"
          ? copy.registration
          : `${copy[family]} (${periods} ${periods === 1 ? copy.month : copy.months})`;
      const capturedRemaining = allocations.flatMap((allocation) =>
        allocation.remainingAfterMad === undefined ? [] : [allocation.remainingAfterMad],
      );
      return [
        {
          family,
          label,
          amountMad: allocations.reduce(
            (sum, allocation) => sum + BigInt(allocation.amountMad),
            0n,
          ),
          remainingAfterMad:
            capturedRemaining.length === allocations.length
              ? capturedRemaining.reduce((sum, value) => sum + BigInt(value), 0n)
              : null,
        },
      ];
    },
  );

  return (
    <section
      className="financial-document"
      data-kind={snapshot.kind}
      data-lifecycle={lifecycle}
      data-reference={snapshot.reference}
      data-testid="financial-document"
      lang={locale}
      dir={locale === "ar" ? "rtl" : "ltr"}
      aria-labelledby="financial-document-title"
    >
      <header className="financial-document__paper-header">
        <div className="financial-document__logos">
          <div className="financial-document__school">
            <img
              className="financial-document__chams-mark"
              src="/brand-logo.png"
              alt=""
              aria-hidden="true"
            />
            <span>
              <strong>{copy.school}</strong>
              <small>{copy.schoolLatin}</small>
            </span>
          </div>
          <img
            className="financial-document__ministry"
            src={`/logo-ministere-${locale}.svg`}
            alt={copy.ministry}
          />
        </div>
        <hr />
      </header>

      <div className="financial-document__content">
        <div className="financial-document__title">
          <h2 id="financial-document-title">
            {pending ? (
              <>
                <span>{text.acknowledgement}</span>
                <span>{pendingInstrumentTitle}</span>
              </>
            ) : (
              copy.title
            )}
          </h2>
          <strong className="financial-document__status">
            <span aria-hidden="true" />
            {lifecycleLabel}
          </strong>
        </div>

        {lifecycle === "cancelled" && (
          <div className="financial-document__lifecycle" role="status">
            <strong>{text.cancelled}</strong>
            {cancellationReason && (
              <span>
                {text.cancellationReason}: {cancellationReason}
              </span>
            )}
          </div>
        )}

        <div className="financial-document__sections">
          <div className="financial-document__info-grid">
            <section className="financial-document__card financial-document__student-card">
              <h3>{copy.studentDetails}</h3>
              <dl>
                <InfoRow label={copy.fullName}>{studentName}</InfoRow>
                <InfoRow label={copy.class} ltr>
                  <Bidi>{normalizeSchoolLevelLabel(snapshot.student.classLabel)}</Bidi>
                </InfoRow>
              </dl>
              {academicYear && (
                <div className="financial-document__academic-year">
                  <span>{copy.academicYear}</span>
                  <strong className="financial-document__number" dir="ltr">
                    <Bidi>{academicYear}</Bidi>
                  </strong>
                </div>
              )}
            </section>

            <section className="financial-document__card">
              <h3>{copy.paymentDetails}</h3>
              <dl>
                <InfoRow label={copy.reference} ltr>
                  <Bidi>{snapshot.reference}</Bidi>
                </InfoRow>
                <InfoRow label={copy.received} ltr>
                  <Bidi>{formatDisplayDate(snapshot.receivedDate)}</Bidi>
                </InfoRow>
                {settlementDate && settlementDate !== snapshot.receivedDate && (
                  <InfoRow label={copy.settled} ltr>
                    <Bidi>{formatDisplayDate(settlementDate)}</Bidi>
                  </InfoRow>
                )}
                {paidPeriods.length > 0 && (
                  <div className="financial-document__info-row financial-document__periods-row">
                    <dt>{copy.periods}</dt>
                    <dd>
                      <span className="financial-document__period-pills">
                        {paidPeriods.map((period) => (
                          <span className="financial-document__period-pill" key={period.key}>
                            {period.label}
                          </span>
                        ))}
                      </span>
                    </dd>
                  </div>
                )}
                <InfoRow label={copy.method}>{method}</InfoRow>
              </dl>
            </section>
          </div>

          <section className="financial-document__card financial-document__services">
            <h3>{copy.services}</h3>
            <dl>
              {serviceRows.map((service) => {
                const remainingAfterMad =
                  lifecycle === "settled" &&
                  service.remainingAfterMad !== null &&
                  service.remainingAfterMad > 0n
                    ? service.remainingAfterMad
                    : null;
                return (
                  <Fragment key={service.family}>
                    <InfoRow
                      label={
                        remainingAfterMad !== null
                          ? `${service.label} · ${text.partial}`
                          : service.label
                      }
                      ltr
                      {...(remainingAfterMad !== null
                        ? { className: "financial-document__service-partial-main" }
                        : {})}
                    >
                      {formatWholeFigure(service.amountMad)} <small>{copy.currency}</small>
                    </InfoRow>
                    {remainingAfterMad !== null && (
                      <>
                        <InfoRow
                          label={text.remaining}
                          ltr
                          className="financial-document__service-partial-subrow"
                        >
                          {formatWholeFigure(remainingAfterMad)} <small>{copy.currency}</small>
                        </InfoRow>
                        <InfoRow
                          label={text.total}
                          ltr
                          className="financial-document__service-partial-subrow financial-document__service-partial-subrow--last"
                        >
                          {formatWholeFigure(service.amountMad + remainingAfterMad)}{" "}
                          <small>{copy.currency}</small>
                        </InfoRow>
                      </>
                    )}
                  </Fragment>
                );
              })}
            </dl>
          </section>

          <section className="financial-document__total">
            <strong>{copy.total}</strong>
            <span className="financial-document__number" dir="ltr">
              {amount(snapshot.amountMad)} <small>{copy.currency}</small>
            </span>
          </section>

          <div className="financial-document__signature">
            <span>{copy.signature}</span>
          </div>
        </div>
      </div>

      <footer className="financial-document__contact">
        <hr />
        <div>
          <span>
            <ContactIcon kind="home" />
            {copy.address}
          </span>
          <span>
            <ContactIcon kind="mail" />
            <bdi>chams.alouloum@gmail.com</bdi>
          </span>
          <span>
            <ContactIcon kind="phone" />
            <bdi className="financial-document__number">05 28 77 14 50 | 06 70 65 34 31</bdi>
          </span>
        </div>
      </footer>
    </section>
  );
}
